import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PublishOptions, StorePlatform } from '@/types/store';

import { PLATFORM_LABELS } from './store-constants';

interface PublishConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: StorePlatform;
  /** What is being pushed, e.g. "the en-US listing" or "2 en-US screenshots". */
  target: string;
  isPending: boolean;
  onConfirm: (options: PublishOptions) => void;
}

/**
 * Confirmation gate for a publish. Publishing is the deliberate approval step —
 * it pushes saved copy live (through store review). For Google Play only, an
 * advanced toggle can stage the change without sending it to review; the current
 * app rejects that flag, so it is off by default and flagged with a warning.
 */
export function PublishConfirmDialog({
  open,
  onOpenChange,
  platform,
  target,
  isPending,
  onConfirm,
}: PublishConfirmDialogProps): JSX.Element {
  const isGoogle = platform === 'google_play';
  const [notForReview, setNotForReview] = useState(false);

  useEffect(() => {
    if (open) setNotForReview(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publish to {PLATFORM_LABELS[platform]}?</DialogTitle>
          <DialogDescription>
            This pushes {target} to the store, through {PLATFORM_LABELS[platform]} review. It is a
            live change — not a draft.
          </DialogDescription>
        </DialogHeader>

        {isGoogle && (
          <label className="flex items-start gap-2 rounded-md border border-border bg-secondary/40 p-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={notForReview}
              onChange={(e) => setNotForReview(e.target.checked)}
            />
            <span>
              <span className="font-medium text-foreground">Stage without sending to review</span>
              <span className="mt-1 flex items-center gap-1 text-xs text-status-degraded">
                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                Apps set to auto-review reject this flag (400). Leave off unless you know the app
                allows it.
              </span>
            </span>
          </label>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isPending}
            onClick={() => onConfirm(isGoogle ? { changesNotSentForReview: notForReview } : {})}
          >
            {isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
